<template>
  <div class="knowledge-course-display">
    <div class="display-header">
      <div class="header-left">
        <button class="back-button" @click="goBack">
          <span class="back-icon">←</span>
          返回课程列表
        </button>
        <div class="header-title-row">
          <h2>{{ currentCourse?.name || '知识课程' }}</h2>
          <span v-if="currentCourse?.level" class="title-tag title-tag--level">{{ getLevelText(currentCourse.level) }}</span>
          <span v-if="currentCourse?.category" class="title-tag title-tag--category">{{ currentCourse.category }}</span>
        </div>
      </div>
      <div class="header-right">
        <input
          type="text"
          v-model="searchKeyword"
          class="search-bar__input"
          placeholder="查找知识点..."
          @input="handleSearch"
        >
        <div class="header-actions">
          <el-tooltip content="刷新" placement="top">
            <el-button circle :loading="refreshing" @click="refreshPage">
              <el-icon><Refresh /></el-icon>
            </el-button>
          </el-tooltip>
          <el-tooltip content="复位" placement="top">
            <el-button circle @click="resetView">
              <el-icon><RefreshLeft /></el-icon>
            </el-button>
          </el-tooltip>
        </div>
      </div>
    </div>

    <div v-if="canAuditOnDisplay" class="admin-preview-banner">
      <span>{{ previewBanner || '管理预览' }}</span>
      <span class="admin-preview-actions">
        <el-button
          v-if="Number(currentCourse.auditStatus) !== 1"
          size="small"
          type="success"
          :loading="auditSaving"
          @click="auditCourse(1)"
        >通过</el-button>
        <el-button
          v-if="Number(currentCourse.auditStatus) !== 2"
          size="small"
          type="danger"
          :loading="auditSaving"
          @click="auditCourse(2)"
        >驳回</el-button>
      </span>
    </div>
    <div v-else-if="creatorPreviewBanner" class="admin-preview-banner">{{ creatorPreviewBanner }}</div>

    <div v-if="accessBlocked" class="access-blocked">
      <p class="access-blocked__message">{{ accessMessage }}</p>
      <div v-if="accessAction === 'buy'" class="redeem-box">
        <p>创作者发给你的兑换码可以在这里核销。核销后即可学习，和余额购买分开记录。</p>
        <el-input
          v-model="redeemCode"
          placeholder="兑换码"
          maxlength="32"
          @keyup.enter="submitRedeem"
        />
        <el-button type="primary" :loading="redeeming" @click="submitRedeem">核销兑换码</el-button>
      </div>
      <div class="access-blocked__actions">
        <el-button @click="goBack">返回课程列表</el-button>
        <el-button v-if="accessAction === 'login'" type="primary" @click="goLogin">登录</el-button>
        <el-button v-else-if="accessAction === 'buy'" type="primary" :loading="buying" @click="buyCourse">余额购买</el-button>
      </div>
    </div>

    <div v-else class="course-container">
      <!-- Course Tree -->
      <div class="course-tree">
        <h3>知识点列表</h3>
        <div class="tree-container">
          <div v-if="currentCourse && currentCourse.kpTree" class="kp-tree">
            <div v-for="(node, index) in filteredKpTree" :key="index" class="kp-node">
              <div 
                class="kp-header"
                :class="{ 'active': selectedKp?.kpId === node.kpID }"
                @click="selectKnowledgePoint(node)"
              >
                <span class="kp-icon">📖</span>
                <span class="kp-name">{{ node.name || '未命名知识点' }}</span>
                <span class="kp-seq">{{ node.seqname }}</span>
              </div>
              
              <!-- Child Knowledge Points -->
              <div v-if="node.children && node.children.length" class="kp-children">
                <div v-for="(child, childIndex) in node.children" :key="childIndex" class="kp-child">
                  <div 
                    class="kp-header"
                    :class="{ 'active': selectedKp?.kpId === child.kpID }"
                    @click="selectKnowledgePoint(child)"
                  >
                    <span class="kp-icon">📝</span>
                    <span class="kp-name">{{ child.name || '未命名知识点' }}</span>
                    <span class="kp-seq">{{ child.seqname }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="loading" class="loading">
            <p>加载中...</p>
          </div>
          <div v-else class="no-course">
            <p>课程不存在或加载失败</p>
          </div>
        </div>
      </div>

      <!-- Knowledge Point Content -->
      <div class="content-panel">
        <div v-if="selectedKp" class="kp-content">
          <div class="kp-content-title">
            <h3>{{ selectedKp.name }}</h3>
            <span v-if="selectedKp.category" class="title-tag title-tag--category">{{ selectedKp.category }}</span>
            <span v-if="selectedKp.level" class="title-tag title-tag--level">{{ getLevelText(selectedKp.level) }}</span>
            <el-button
              v-if="isLoggedIn"
              size="small"
              :type="isSelectedMastered ? 'success' : 'primary'"
              plain
              @click="toggleMastered"
            >
              {{ isSelectedMastered ? '已掌握' : '标记已掌握' }}
            </el-button>
          </div>
          <div class="kp-content-body">
            <div v-if="prerequisites.length > 0" class="content-section content-section--compact">
              <div class="prerequisites-list">
                <div v-for="(prereq, index) in prerequisites" :key="index" class="prerequisite-item">
                  <div class="prerequisite-header">
                    <span class="prerequisite-icon">📚</span>
                    <span class="prerequisite-name">{{ prereq.name }}</span>
                    <span v-if="prereq.category" class="title-tag title-tag--category title-tag--sm">{{ prereq.category }}</span>
                    <span v-if="prereq.level" class="title-tag title-tag--level title-tag--sm">{{ getLevelText(prereq.level) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedKp.content && String(selectedKp.content).trim()" class="content-section content-section--compact">
              <div class="content-text" v-html="safeRichHtml(selectedKp.content)"></div>
            </div>

            <div v-if="selectedKp.examples && selectedKp.examples.length" class="content-section content-section--compact">
              <div v-for="(example, index) in selectedKp.examples" :key="index" class="example-item">
                <p>{{ example }}</p>
              </div>
            </div>

            <div v-if="selectedKp.references && selectedKp.references.length" class="content-section content-section--compact">
              <ul>
                <li v-for="(ref, index) in selectedKp.references" :key="index">
                  {{ ref }}
                </li>
              </ul>
            </div>

            <div v-if="learningResources.length > 0" class="content-section content-section--fill">
              <div class="learning-resources-list">
                <div v-for="(resource, index) in learningResources" :key="index" class="learning-resource-item">
                  <div class="resource-content">
                    <div v-if="resource.content && String(resource.content).trim()" class="text-content" v-html="safeRichHtml(resource.content)"></div>

                    <!-- 哔哩哔哩等嵌入：只渲染 iframe，不用 video tag -->
                    <div
                      v-if="hasEmbedIframe(resource)"
                      class="iframe-container"
                      v-html="safeRichHtml(resource.iframe)"
                    ></div>
                    <div v-else-if="videoOptions[resource.krId]">
                      <video-player :options="videoOptions[resource.krId]" :is-audio="resource.type === 'audio'" />
                    </div>
                    <div v-else-if="resource.type === 'video' && resource.url">
                      <video
                        :src="resource.url"
                        controls
                        class="video-player"
                        preload="metadata"
                      >
                        您的浏览器不支持视频播放
                      </video>
                    </div>
                    <div v-else-if="resource.type === 'audio' && resource.url">
                      <audio
                        :src="resource.url"
                        controls
                        class="audio-player"
                        preload="metadata"
                      >
                        您的浏览器不支持音频播放
                      </audio>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="no-selection">
          <div class="no-selection-content">
            <span class="no-selection-icon">📚</span>
            <h3>选择知识点</h3>
            <p>请从左侧知识点列表中选择一个知识点来查看详细内容</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VideoPlayer from '@/components/VideoPlayer.vue';
import { Refresh, RefreshLeft } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { sanitizeRichHtml } from '@/utils/sanitizeHtml'
import { canViewCourseContent } from '@/utils/courseAccess'
import { goToLogin } from '@/utils/loginFlow'
import { isSystemAdmin } from '@/utils/sysRole'

export default {
  name: 'KnowledgeCourseDisplay',
  components: {
    VideoPlayer,
    Refresh,
    RefreshLeft
  },
  data() {
    return {
      currentCourse: null,
      knowledgePoints: [],
      selectedKp: null,
      searchKeyword: '',
      loading: true,
      refreshing: false,
      accessBlocked: false,
      accessMessage: '',
      accessAction: '',
      prerequisites: [],
      allPrerequisites: [],
      learningResources: [],
      videoOptions: [],
      masteredKpIds: {},
      auditSaving: false,
      redeemCode: '',
      redeeming: false,
      buying: false
    }
  },
  computed: {
    courseKcId() {
      return parseInt(this.$route.params.courseId, 10) || 0
    },
    isLoggedIn() {
      return !!localStorage.getItem('jwt_token')
    },
    isAdminPreview() {
      return String(this.$route.query.adminView || '') === '1'
    },
    fromCreatorWorks() {
      return String(this.$route.query.from || '') === 'works'
    },
    canAuditOnDisplay() {
      return this.isAdminPreview && this.isLoggedInAdmin() && !!this.currentCourse
    },
    creatorPreviewBanner() {
      if (!this.currentCourse || !this.isCourseOwner(this.currentCourse)) return ''
      const status = Number(this.currentCourse.auditStatus)
      const removed = Number(this.currentCourse.removed) === 1
      const offShelf = Number(this.currentCourse.state) === 0
      if (!removed && !offShelf && status === 1) return ''
      const parts = []
      if (removed) parts.push('已删除')
      else if (offShelf) parts.push('已下架')
      if (status === 0) parts.push('待审核')
      else if (status === 2) parts.push('已驳回')
      if (!parts.length) return '这是你的课程预览，公开列表暂不展示'
      return `这是你的课程预览：${parts.join('、')}，公开列表暂不展示`
    },
    previewBanner() {
      if (!this.canAuditOnDisplay) return ''
      const status = Number(this.currentCourse.auditStatus)
      const removed = Number(this.currentCourse.removed) === 1
      const offShelf = Number(this.currentCourse.state) === 0
      if (!removed && !offShelf && status === 1) return ''
      const parts = []
      if (removed) parts.push('已删除')
      else if (offShelf) parts.push('已下架')
      if (status === 0) parts.push('待审核')
      else if (status === 2) parts.push('已驳回')
      if (!parts.length) return '管理预览：前台用户看不到本课'
      return `管理预览：${parts.join('、')}，前台用户看不到本课`
    },
    isSelectedMastered() {
      const id = this.selectedKp?.kpId
      return !!(id && this.masteredKpIds[id])
    },
    filteredKpTree() {
      if (!this.currentCourse?.kpTree?.children) {
        return []
      }
      
      if (!this.searchKeyword) {
        return this.currentCourse.kpTree.children
      }
      
      const keyword = this.searchKeyword.toLowerCase()
      return this.currentCourse.kpTree.children.filter(node => {
        const nodeMatch = (node.name || '').toLowerCase().includes(keyword)
        const childrenMatch = node.children && Array.isArray(node.children) && node.children.some(child => 
          (child.name || '').toLowerCase().includes(keyword)
        )
        return nodeMatch || childrenMatch
      })
    }
  },
  created() {
    this.fetchCourse()
    this.fetchKnowledgePoints()
    this.fetchAllPrerequisites()
    this.fetchMastered()
  },
  methods: {
    safeRichHtml(html) {
      return sanitizeRichHtml(html || '')
    },
    parseKpTree(raw) {
      if (raw && typeof raw === 'object') {
        return raw.children ? raw : { children: [] }
      }
      if (!raw) {
        return { children: [] }
      }
      try {
        const kpTree = JSON.parse(raw)
        if (!kpTree.children) {
          kpTree.children = []
        }
        return kpTree
      } catch (parseError) {
        console.error('Failed to parse kpTree:', parseError)
        return { children: [] }
      }
    },
    async fetchCourse() {
      try {
        this.loading = true
        const courseId = parseInt(this.$route.params.courseId, 10)
        const token = localStorage.getItem('jwt_token')

        if (this.isAdminPreview && !token) {
          this.$message.error('请先登录后再预览未审核课程')
          return
        }

        if (token) {
          const body = { kcId: courseId }
          if (this.isAdminPreview && this.isLoggedInAdmin()) {
            body.adminView = 1
          }
          const response = await fetch('/api/v1/knowledge/courses/get', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          })
          const data = await response.json()
          if (data.code === 200 && data.data) {
            if (!this.applyCourseAccess(data.data, this.parseKpTree(data.data.kpTree))) {
              return
            }
          } else if (data.code === 401) {
            this.$message.error('登录已过期，请重新登录')
          } else {
            this.$message.error(data.message || '课程不存在')
          }
        } else {
          const response = await fetch('/api/v1/knowledge/courses/guest/list', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              page: 1,
              pageSize: 1000,
              keyword: ''
            })
          })
          const data = await response.json()
          if (data.code === 200) {
            const course = (data.data?.list || []).find(c => c.kcId === courseId)
            if (course) {
              if (!this.applyCourseAccess(course, this.parseKpTree(course.kpTree))) {
                return
              }
            } else {
              this.$message.error('课程不存在')
            }
          } else {
            this.$message.error(data.message || '获取课程信息失败')
          }
        }
      } catch (error) {
        console.error('Failed to fetch course:', error)
        this.$message.error('获取课程信息失败')
      } finally {
        this.loading = false
      }
    },
    async fetchKnowledgePoints() {
      try {
        const token = localStorage.getItem('jwt_token')
        
        // Check if user is logged in
        if (token) {
          // User is logged in, use authenticated endpoint
          const response = await fetch('/api/v1/knowledge/points/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              page: 1,
              pageSize: 1000,
              keyword: '',
              kcId: this.courseKcId
            })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.knowledgePoints = data.data?.list || []
          } else if (data.code === 401) {
            this.$message.error('登录已过期，请重新登录')
          } else {
            this.$message.error(data.message || '获取知识点列表失败')
          }
        } else {
          // User is not logged in, use guest endpoint
          const response = await fetch('/api/v1/knowledge/points/guest/list', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              page: 1,
              pageSize: 1000,
              keyword: '',
              kcId: this.courseKcId
            })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.knowledgePoints = data.data?.list || []
          } else {
            this.$message.error(data.message || '获取知识点列表失败')
          }
        }
      } catch (error) {
        console.error('Failed to fetch knowledge points:', error)
        this.$message.error('获取知识点列表失败')
      }
    },
    async fetchAllPrerequisites() {
      try {
        const token = localStorage.getItem('jwt_token')
        if (token) {
          this.allPrerequisites = []
          return
        }
        const response = await fetch('/api/v1/knowledge/prerequisites/guest/list', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 1000
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.allPrerequisites = data.data?.list || []
        }
      } catch (error) {
        console.error('Failed to fetch prerequisites:', error)
      }
    },
    selectKnowledgePoint(node) {
      if (node.kpID) {
        const kp = (this.knowledgePoints || []).find(kp => kp.kpId === node.kpID)
        if (kp) {
          this.selectedKp = kp
          this.loadPrerequisites(kp.kpId)
          this.loadLearningResources(kp.kpId)
        } else {
          this.$message.warning('知识点信息不存在')
        }
      } else {
        this.$message.warning('该节点未关联知识点')
      }
    },
    async loadPrerequisites(kpId) {
      const mapRows = (rows) => (rows || []).map(p => {
        const point = (this.knowledgePoints || []).find(kp => kp.kpId === p.parentKpId)
        return point || { kpId: p.parentKpId, name: '未知知识点', category: '未知', level: 1 }
      })
      const cached = (this.allPrerequisites || []).filter(p => p.childKpId === kpId)
      if (cached.length) {
        this.prerequisites = mapRows(cached)
        return
      }
      try {
        const token = localStorage.getItem('jwt_token')
        const url = token ? '/api/v1/knowledge/prerequisites/list' : '/api/v1/knowledge/prerequisites/guest/list'
        const headers = { 'Content-Type': 'application/json' }
        if (token) headers.Authorization = `Bearer ${token}`
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ kpId, page: 1, pageSize: 1000 })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.prerequisites = mapRows(data.data?.list || [])
        } else {
          this.prerequisites = []
        }
      } catch (e) {
        console.error(e)
        this.prerequisites = []
      }
    },
    async loadLearningResources(kpId) {
      try {
        const token = localStorage.getItem('jwt_token')
        
        // Check if user is logged in
        if (token) {
          // User is logged in, use authenticated endpoint
          const response = await fetch('/api/v1/knowledge/resources/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              kpId: kpId,
              kcId: this.courseKcId,
              page: 1,
              pageSize: 1000,
              keyword: ''
            })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.learningResources = data.data?.list || []
            if (data.data?.total === 0) {
              this.learningResources = []
            }
          } else if (data.code === 401) {
            this.$message.error('登录已过期，请重新登录')
          } else {
            console.error('Failed to fetch learning resources:', data.message)
            this.learningResources = []
          }
        } else {
          // User is not logged in, use guest endpoint
          const response = await fetch('/api/v1/knowledge/resources/guest/list', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              kpId: kpId,
              page: 1,
              pageSize: 1000,
              keyword: ''
            })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.learningResources = data.data?.list || []
            if (data.data?.total === 0) {
              this.learningResources = []
            }
          } else {
            console.error('Failed to fetch learning resources:', data.message)
            this.learningResources = []
          }
        }
      } catch (error) {
        console.error('Failed to fetch learning resources:', error)
        this.learningResources = []
      }
      var arr = [];
      this.learningResources.forEach((r) => {
        if (!r.url)return;
        const isM3u8 = r.url.endsWith('.m3u8');
        if (!isM3u8)return;
        arr[r.krId] ={
          fluid: true,         
          autoplay: false,
          controls: true,
          preload: 'auto',    
          sources: [{
              src: r.url,
              type: 'application/x-mpegURL'
          }]
        };
      });
      this.videoOptions = arr;
    },
    handleSearch() {
      // Search is handled by computed property
    },
    getLevelText(level) {
      const levels = {
        1: '初级',
        2: '中级',
        3: '高级'
      }
      return levels[level] || '未知'
    },
    /** 有嵌入 iframe（如哔哩哔哩）时只播 iframe，不走 video/audio tag */
    hasEmbedIframe(resource) {
      return !!(resource && String(resource.iframe || '').trim())
    },
    getResourceIcon(type) {
      const icons = {
        video: '📺',
        document: '📄',
        text: '📄',
        iframe: '🌐'
      }
      return icons[type] || '📄'
    },
    getResourceTypeText(type) {
      const types = {
        video: '视频',
        document: '文档',
        text: '文本',
        iframe: '网页嵌入'
      }
      return types[type] || '文本'
    },
    getDifficultyText(difficulty) {
      const difficulties = {
        1: '初级',
        2: '中级',
        3: '高级'
      }
      return difficulties[difficulty] || '未知'
    },
    formatDate(date) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(date).toLocaleDateString(undefined, options)
    },
    truncateUrl(url) {
      if (!url) return ''
      const maxLength = 50;
      if (url.length > maxLength) {
        return url.slice(0, maxLength) + '...';
      }
      return url;
    },
    isCourseOwner(course) {
      const uuid = (localStorage.getItem('uuid') || '').trim()
      return !!uuid && !!course && uuid === String(course.creator || '').trim()
    },
    isLoggedInAdmin() {
      try {
        const user = JSON.parse(localStorage.getItem('user_info') || '{}')
        return isSystemAdmin(user.sysRole)
      } catch (e) {
        return false
      }
    },
    applyCourseAccess(course, kpTree) {
      const adminPreview = this.isAdminPreview && this.isLoggedInAdmin()
      if (adminPreview || this.isCourseOwner(course) || canViewCourseContent(course)) {
        this.accessBlocked = false
        this.accessMessage = ''
        this.accessAction = ''
        this.currentCourse = { ...course, kpTree }
        return true
      }
      this.accessBlocked = true
      this.currentCourse = { ...course, kpTree }
      const token = localStorage.getItem('jwt_token')
      if (!token) {
        this.accessMessage = '该课程为付费内容，登录并购买后可学习'
        this.accessAction = 'login'
      } else {
        this.accessMessage = `该课程为付费内容（${this.formatCoursePrice(course.fee)}）。可以用余额购买，或核销创作者发给你的兑换码。`
        this.accessAction = 'buy'
      }
      return false
    },

    formatCoursePrice(fee) {
      if (!fee) return '免费'
      return `¥${(fee / 100).toFixed(2)}`
    },

    goLogin() {
      goToLogin(this.$router, this.$route.fullPath)
    },

    async submitRedeem() {
      const code = (this.redeemCode || '').trim()
      if (!code) {
        this.$message.warning('请填写兑换码')
        return
      }
      this.redeeming = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/courses/redeem', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ kcId: this.courseKcId, code })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.$message.success(data.message || '兑换成功，可以开始学习')
          this.redeemCode = ''
          await this.fetchCourse()
        } else {
          this.$message.error(data.message || '核销失败')
        }
      } catch (e) {
        this.$message.error('核销失败')
      } finally {
        this.redeeming = false
      }
    },

    async buyCourse() {
      const course = this.currentCourse
      if (!course || !course.fee) return
      try {
        await ElMessageBox.confirm(
          `确定用余额购买此课程吗？费用：${this.formatCoursePrice(course.fee)}`,
          '购买确认',
          { confirmButtonText: '确定购买', cancelButtonText: '取消', type: 'info' }
        )
      } catch (e) {
        return
      }
      this.buying = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/courses/buy', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ kcId: this.courseKcId })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.$message.success('购买成功，开始学习')
          await this.fetchCourse()
        } else {
          this.$message.error(data.message || '购买失败')
        }
      } catch (e) {
        this.$message.error('购买失败')
      } finally {
        this.buying = false
      }
    },

    async fetchMastered() {
      if (!this.isLoggedIn) {
        this.masteredKpIds = {}
        return
      }
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/mastered/list', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ page: 1, pageSize: 500 })
        })
        const data = await response.json()
        const map = {}
        if (data.code === 200) {
          (data.data?.list || []).forEach((row) => {
            if (row.kpId && (row.score || 0) >= 80) {
              map[row.kpId] = true
            }
          })
        }
        this.masteredKpIds = map
      } catch (e) {
        console.error('Failed to fetch mastered:', e)
      }
    },

    async toggleMastered() {
      if (!this.isLoggedIn || !this.selectedKp?.kpId) return
      const kpId = this.selectedKp.kpId
      const nextScore = this.isSelectedMastered ? 0 : 100
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/mastered/update', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ kpId, score: nextScore })
        })
        const data = await response.json()
        if (data.code === 200) {
          const map = { ...this.masteredKpIds }
          if (nextScore >= 80) {
            map[kpId] = true
          } else {
            delete map[kpId]
          }
          this.masteredKpIds = map
          this.$message.success(nextScore >= 80 ? '已标记掌握' : '已取消掌握')
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '更新掌握状态失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('更新掌握状态失败')
      }
    },

    async refreshPage() {
      this.refreshing = true
      try {
        await this.fetchCourse()
        await Promise.all([
          this.fetchKnowledgePoints(),
          this.fetchAllPrerequisites(),
          this.fetchMastered()
        ])
        if (this.selectedKp?.kpId) {
          await this.loadLearningResources(this.selectedKp.kpId)
          await this.loadPrerequisites(this.selectedKp.kpId)
        }
        ElMessage.success('已刷新')
      } finally {
        this.refreshing = false
      }
    },

    resetView() {
      this.searchKeyword = ''
      this.selectedKp = null
      this.prerequisites = []
      this.learningResources = []
      this.videoOptions = []
      ElMessage.success('已复位')
    },

    async auditCourse(auditStatus) {
      const course = this.currentCourse
      if (!course) return
      let auditMessage = ''
      try {
        if (auditStatus === 1) {
          await ElMessageBox.confirm(
            `确定通过课程「${course.name || course.kcId}」的审核？通过后若已上架，将出现在公开列表。`,
            '审核通过',
            { type: 'success', confirmButtonText: '通过', cancelButtonText: '取消' }
          )
          auditMessage = '审核通过'
        } else {
          const { value } = await ElMessageBox.prompt(
            '请填写驳回原因（将通知创作者）',
            `驳回「${course.name || course.kcId}」`,
            {
              type: 'warning',
              confirmButtonText: '驳回',
              cancelButtonText: '取消',
              inputPlaceholder: '例如：内容不完整、封面缺失',
              inputValidator: (v) => !!(v && String(v).trim()) || '请填写驳回原因'
            }
          )
          auditMessage = String(value || '').trim()
        }
      } catch {
        return
      }
      this.auditSaving = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/admin/knowledge/course/audit', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            kcId: course.kcId,
            auditStatus,
            auditMessage
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.$message.success(data.message || (auditStatus === 1 ? '已通过' : '已驳回'))
          await this.fetchCourse()
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '审核失败')
        }
      } catch (error) {
        this.$message.error('审核失败')
      } finally {
        this.auditSaving = false
      }
    },
    goBack() {
      if (this.isAdminPreview && this.isLoggedInAdmin()) {
        this.$router.push('/admin/knowledge/courses')
        return
      }
      if (this.fromCreatorWorks) {
        this.$router.push('/creator/works')
        return
      }
      this.$router.push('/knowledge/courses')
    }
  }
}
</script>

<style scoped>
.knowledge-course-display {
  padding: 16px 24px 20px 20px;
  width: 100%;
  max-width: none;
  margin: 0;
  height: calc(100vh - 60px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.admin-preview-banner {
  flex-shrink: 0;
  margin: 0 0 12px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fdf6ec;
  color: #b88230;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-preview-actions {
  display: flex;
  gap: 8px;
}

.access-blocked {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  text-align: center;
}

.access-blocked__message {
  margin: 0;
  font-size: 16px;
  color: #606266;
  max-width: 420px;
  line-height: 1.6;
}

.access-blocked__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

.redeem-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(360px, 100%);
  text-align: left;
}
.redeem-box p {
  margin: 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}

.display-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 2px solid #eee;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: nowrap;
}

.header-title-row h2 {
  margin: 0;
  color: #333;
  font-size: 18px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-tag {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.title-tag--sm {
  padding: 1px 6px;
  font-size: 11px;
}

.title-tag--level {
  background: #f3e5f5;
  color: #7b1fa2;
}

.title-tag--category {
  background: #e3f2fd;
  color: #1976d2;
}

.kp-content-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 12px 24px;
  border-bottom: 1px solid #eee;
  min-width: 0;
}

.kp-content-title h3 {
  margin: 0;
  color: #333;
  font-size: 16px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: #666;
}

.back-button:hover {
  background: #e8e8e8;
  border-color: #ccc;
}

.back-icon {
  font-size: 16px;
  font-weight: bold;
}

.display-header h2 {
  margin: 0;
  color: #333;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-bar__input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 250px;
  font-size: 14px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.course-container {
  display: flex;
  flex: 1;
  gap: 20px;
  overflow: hidden;
}

.course-tree {
  width: 350px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  overflow-y: auto;
}

.course-tree h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
}

.tree-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.loading, .no-course {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.loading p, .no-course p {
  margin: 0;
  font-size: 14px;
}

.kp-tree {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.kp-node {
  background: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.course-node {
  background: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.course-header {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #eee;
}

.course-header:hover {
  background: #f0f8ff;
}

.course-header.active {
  background: #e3f2fd;
  border-left: 4px solid #2196F3;
}

.course-icon {
  margin-right: 10px;
  font-size: 16px;
}

.course-name {
  flex: 1;
  font-weight: 500;
  color: #333;
}

.course-level {
  background: #e8f5e8;
  color: #2e7d32;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.kp-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  margin-bottom: 5px;
}

.kp-header:hover {
  background: #f0f0f0;
}

.kp-header.active {
  background: #e8f4fd;
  border-left: 3px solid #1976d2;
}

.kp-icon {
  margin-right: 8px;
  font-size: 14px;
}

.kp-name {
  flex: 1;
  font-size: 14px;
  color: #555;
}

.kp-seq {
  background: #f0f0f0;
  color: #666;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
}

.kp-children {
  margin-left: 20px;
  padding-left: 10px;
  border-left: 2px solid #e0e0e0;
}

.kp-child .kp-header {
  padding: 6px 10px;
  font-size: 13px;
}

.content-panel {
  flex: 1;
  min-height: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.kp-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.kp-content-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 12px 24px 24px 12px;
  box-sizing: border-box;
}

.content-section {
  margin-bottom: 12px;
}

.content-section--compact {
  flex-shrink: 0;
  padding: 12px 0 0;
}

.content-section--fill {
  flex: 1;
  min-height: 0;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.content-section h4 {
  color: #333;
  margin: 0 0 10px 0;
  font-size: 18px;
  border-left: 4px solid #2196F3;
  padding-left: 12px;
}

.content-section p {
  color: #555;
  line-height: 1.6;
  margin: 0;
}

.content-text {
  color: #555;
  line-height: 1.6;
}

.example-item {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 10px;
  border-left: 4px solid #4caf50;
}

.example-item h5 {
  margin: 0 0 8px 0;
  color: #2e7d32;
  font-size: 16px;
}

.example-item p {
  margin: 0;
  color: #555;
}

.content-section ul {
  margin: 0;
  padding-left: 20px;
}

.content-section li {
  color: #555;
  line-height: 1.6;
  margin-bottom: 5px;
}

/* 前置知识点样式 */
.prerequisites-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prerequisite-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.2s ease;
}

.prerequisite-item:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.prerequisite-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.prerequisite-icon {
  font-size: 16px;
}

.prerequisite-name {
  flex: 1;
  font-weight: 500;
  color: #333;
  font-size: 16px;
}

.prerequisite-category {
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.prerequisite-level {
  background: #f3e5f5;
  color: #7b1fa2;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.prerequisite-description {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e9ecef;
}

/* 学习资源样式 */
.learning-resources-list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
}

.learning-resource-item {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.resource-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
}

.resource-icon {
  font-size: 20px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.resource-type {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.resource-difficulty {
  background: #fff3e0;
  color: #f57c00;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.resource-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.video-content, .document-content {
  background: #f5f5f5;
  border: 2px dashed #ddd;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  color: #666;
}

.video-player {
  width: 100%;
  max-width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  object-fit: contain;
  border-radius: 0;
  box-shadow: none;
}

.video-placeholder, .document-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.video-icon, .document-icon {
  font-size: 24px;
}

.text-content {
  color: #555;
  
  
  padding-bottom: 12px;
  
}

.resource-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.resource-date {
  color: #999;
  font-size: 12px;
}

.resource-url-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.resource-url-label {
  color: #666;
  font-size: 12px;
  font-weight: 500;
}

.resource-url-link {
  color: #1976d2;
  text-decoration: none;
  font-size: 12px;
  word-break: break-all;
  transition: color 0.2s ease;
}

.resource-url-link:hover {
  color: #1565c0;
  text-decoration: underline;
}

.no-resources {
  color: #6c757d;
  font-style: italic;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #6c757d;
}

.no-selection {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  margin: 16px 24px 24px;
  border-radius: 8px;
}

.no-selection-content {
  text-align: center;
  color: #999;
}

.no-selection-icon {
  font-size: 48px;
  margin-bottom: 15px;
  display: block;
}

.no-selection-content h3 {
  margin: 0 0 10px 0;
  color: #666;
}

.no-selection-content p {
  margin: 0;
  color: #999;
}

/* Responsive Design */
@media (max-width: 768px) {
  .knowledge-course-display {
    padding: 12px 16px;
  }

  .course-container {
    flex-direction: column;
  }
  
  .course-tree {
    width: 100%;
    max-height: 300px;
  }
  
  .display-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .header-right {
    flex-wrap: wrap;
    width: 100%;
  }

  .search-bar__input {
    width: 100%;
    flex: 1;
    min-width: 0;
  }
  
  .kp-content-title {
    padding: 10px 16px;
  }

  .kp-content-body {
    padding: 8px 16px 16px 8px;
  }

  .no-selection {
    margin: 12px 16px 16px;
  }

  /* 前置知识点移动端适配 */
  .prerequisite-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .prerequisite-name {
    font-size: 14px;
  }
  
  .prerequisite-category,
  .prerequisite-level {
    font-size: 11px;
    padding: 1px 6px;
  }
  
  .prerequisite-item {
    padding: 12px;
  }
  
  /* 学习资源移动端适配 */
  .resource-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .resource-icon {
    font-size: 18px;
  }
  
  .resource-name {
    font-size: 14px;
  }
  
  .resource-type,
  .resource-difficulty {
    font-size: 12px;
    padding: 2px 8px;
  }
  
  .learning-resource-item {
    padding: 12px;
  }
  
  .resource-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .resource-url-section {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .resource-url-link {
    font-size: 11px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .video-content,
  .document-content {
    padding: 15px;
  }
  
  .video-icon,
  .document-icon {
    font-size: 20px;
  }
  .iframe-content {
    width: 100%;
    height: 100%;
  }
}

.iframe-container {
  flex: 1;
  width: 100%;
  min-height: 0;
  height: 100%;
  margin: 0;
  border: none;
  border-radius: 0;
  overflow: hidden;
  background: #fff;
}

.iframe-container iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 0;
}

.iframe-container :deep(iframe) {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 0;
}

/* 确保iframe容器内的所有内容都能正确显示 */
.iframe-container :deep(*) {
  max-width: 100%;
  box-sizing: border-box;
}
.resource-name {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  display: flex;
  align-items: center;
}

iframe {
  height: 100%;
  width: 100%;
}
</style> 